import React, { useState, useRef, useCallback, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import './TimetableEditor.css';

interface TimetableEvent {
    id: string;
    title: string;
    start: string;
    end: string;
    backgroundColor?: string;
    borderColor?: string;
    extendedProps: {
        teacher?: string;
        room?: string;
        type: 'lesson' | 'break' | 'lunch' | 'assembly';
    };
}

interface TimetableEditorProps {
    targetType: 'classroom' | 'yearGroup';
    targetId: string;
    targetName: string;
    term: string;
    weekNumber: number;
    weekStartDate: string; // YYYY-MM-DD format for Monday
    initialEvents?: any[]; // Events passed from parent (management screen)
    onClose: () => void;
    onSave?: (events: any[]) => Promise<void>; // External save handler
    embedded?: boolean; // If embedded in management screen (no header)
}

// Color mapping for lesson types (constant, defined outside component)
const typeColors = {
    lesson: { bg: '#5041BC', border: '#3D2F96' },
    break: { bg: '#F7B84B', border: '#E5A73A' },
    lunch: { bg: '#6FCF97', border: '#5BB980' },
    assembly: { bg: '#56CCF2', border: '#45B8DE' }
};

// Helper to format date as local ISO string (without UTC conversion)
// This preserves the local time that was selected in the calendar
const toLocalISOString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const TimetableEditor: React.FC<TimetableEditorProps> = ({
    targetType,
    targetId,
    targetName,
    term,
    weekNumber,
    weekStartDate,
    initialEvents,
    onClose,
    onSave,
    embedded = false
}) => {
    const calendarRef = useRef<FullCalendar>(null);
    const [events, setEvents] = useState<TimetableEvent[]>([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState<{ start: Date; end: Date } | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<TimetableEvent | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [hasChanges, setHasChanges] = useState(false);

    // Form state for new/edit lesson
    const [lessonForm, setLessonForm] = useState({
        title: '',
        teacher: '',
        room: '',
        type: 'lesson' as 'lesson' | 'break' | 'lunch' | 'assembly'
    });

    const loadTimetable = useCallback(async () => {
        setIsLoading(true);
        try {
            const timetableId = `${targetType}_${targetId}_${term.replace(/\s+/g, '_')}_week${weekNumber}`;
            const docRef = doc(db, 'timetables', timetableId);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                const loadedEvents = (data.events || []).map((evt: any) => ({
                    id: evt.id,
                    title: evt.title,
                    start: evt.start,
                    end: evt.end,
                    backgroundColor: typeColors[evt.type as keyof typeof typeColors]?.bg || typeColors.lesson.bg,
                    borderColor: typeColors[evt.type as keyof typeof typeColors]?.border || typeColors.lesson.border,
                    extendedProps: {
                        teacher: evt.teacher || '',
                        room: evt.room || '',
                        type: evt.type || 'lesson'
                    }
                }));
                setEvents(loadedEvents);
            }
        } catch (error) {
            console.error('Error loading timetable:', error);
        } finally {
            setIsLoading(false);
        }
    }, [targetType, targetId, term, weekNumber]);

    // Memoize initialEvents string for dependency comparison
    const initialEventsKey = useMemo(() => {
        return initialEvents !== undefined ? JSON.stringify(initialEvents) : null;
    }, [initialEvents]);

    // Load existing timetable on mount and when initialEvents changes
    React.useEffect(() => {
        if (initialEvents !== undefined) {
            // Use events from parent (management screen)
            const loadedEvents = (initialEvents || []).map((evt: any) => ({
                id: evt.id || `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                title: evt.title || '',
                start: evt.start,
                end: evt.end,
                backgroundColor: typeColors[(evt.type || evt.extendedProps?.type || 'lesson') as keyof typeof typeColors]?.bg || typeColors.lesson.bg,
                borderColor: typeColors[(evt.type || evt.extendedProps?.type || 'lesson') as keyof typeof typeColors]?.border || typeColors.lesson.border,
                extendedProps: {
                    teacher: evt.teacher || evt.extendedProps?.teacher || '',
                    room: evt.room || evt.extendedProps?.room || '',
                    type: evt.type || evt.extendedProps?.type || 'lesson'
                }
            }));
            console.log('[TimetableEditor] Loading initialEvents:', loadedEvents.length, 'events');
            setEvents(loadedEvents);
            setIsLoading(false);
            setHasChanges(false); // Reset changes flag when loading new data
        } else {
            loadTimetable();
        }
    }, [targetId, term, weekNumber, initialEventsKey, loadTimetable]);

    const handleDateSelect = useCallback((selectInfo: any) => {
        setSelectedSlot({
            start: selectInfo.start,
            end: selectInfo.end
        });
        setLessonForm({ title: '', teacher: '', room: '', type: 'lesson' });
        setShowAddModal(true);
    }, []);

    const handleEventClick = useCallback((clickInfo: any) => {
        const event = clickInfo.event;
        setSelectedEvent({
            id: event.id,
            title: event.title,
            start: event.startStr,
            end: event.endStr,
            backgroundColor: event.backgroundColor,
            borderColor: event.borderColor,
            extendedProps: {
                teacher: event.extendedProps.teacher || '',
                room: event.extendedProps.room || '',
                type: event.extendedProps.type || 'lesson'
            }
        });
        setLessonForm({
            title: event.title,
            teacher: event.extendedProps.teacher || '',
            room: event.extendedProps.room || '',
            type: event.extendedProps.type || 'lesson'
        });
        setShowEditModal(true);
    }, []);

    const handleEventDrop = useCallback((dropInfo: any) => {
        const event = dropInfo.event;
        setEvents(prev => prev.map(evt =>
            evt.id === event.id
                ? { ...evt, start: event.startStr, end: event.endStr }
                : evt
        ));
        setHasChanges(true);
    }, []);

    const handleEventResize = useCallback((resizeInfo: any) => {
        const event = resizeInfo.event;
        setEvents(prev => prev.map(evt =>
            evt.id === event.id
                ? { ...evt, start: event.startStr, end: event.endStr }
                : evt
        ));
        setHasChanges(true);
    }, []);

    const addLesson = () => {
        if (!selectedSlot || !lessonForm.title.trim()) return;

        const newEvent: TimetableEvent = {
            id: `evt_${Date.now()}`,
            title: lessonForm.title,
            // Use local ISO string to preserve the selected time without UTC conversion
            start: toLocalISOString(selectedSlot.start),
            end: toLocalISOString(selectedSlot.end),
            backgroundColor: typeColors[lessonForm.type].bg,
            borderColor: typeColors[lessonForm.type].border,
            extendedProps: {
                teacher: lessonForm.teacher,
                room: lessonForm.room,
                type: lessonForm.type
            }
        };

        setEvents(prev => [...prev, newEvent]);
        setShowAddModal(false);
        setSelectedSlot(null);
        setHasChanges(true);
    };

    const updateLesson = () => {
        if (!selectedEvent || !lessonForm.title.trim()) return;

        setEvents(prev => prev.map(evt =>
            evt.id === selectedEvent.id
                ? {
                    ...evt,
                    title: lessonForm.title,
                    backgroundColor: typeColors[lessonForm.type].bg,
                    borderColor: typeColors[lessonForm.type].border,
                    extendedProps: {
                        teacher: lessonForm.teacher,
                        room: lessonForm.room,
                        type: lessonForm.type
                    }
                }
                : evt
        ));
        setShowEditModal(false);
        setSelectedEvent(null);
        setHasChanges(true);
    };

    const deleteLesson = () => {
        if (!selectedEvent) return;
        setEvents(prev => prev.filter(evt => evt.id !== selectedEvent.id));
        setShowEditModal(false);
        setSelectedEvent(null);
        setHasChanges(true);
    };

    // Duplicate an event - creates a copy with a new ID
    const duplicateLesson = () => {
        if (!selectedEvent) return;

        const duplicatedEvent: TimetableEvent = {
            ...selectedEvent,
            id: `evt_${Date.now()}_dup`,
        };

        setEvents(prev => [...prev, duplicatedEvent]);
        setShowEditModal(false);
        setSelectedEvent(null);
        setHasChanges(true);
    };

    // Duplicate to next day
    const duplicateToNextDay = () => {
        if (!selectedEvent) return;

        const startDate = new Date(selectedEvent.start);
        const endDate = new Date(selectedEvent.end);

        // Move to next day
        startDate.setDate(startDate.getDate() + 1);
        endDate.setDate(endDate.getDate() + 1);

        // Check if it's weekend, skip to Monday
        if (startDate.getDay() === 0) { // Sunday
            startDate.setDate(startDate.getDate() + 1);
            endDate.setDate(endDate.getDate() + 1);
        } else if (startDate.getDay() === 6) { // Saturday
            startDate.setDate(startDate.getDate() + 2);
            endDate.setDate(endDate.getDate() + 2);
        }

        const duplicatedEvent: TimetableEvent = {
            ...selectedEvent,
            id: `evt_${Date.now()}_dup`,
            start: toLocalISOString(startDate),
            end: toLocalISOString(endDate),
        };

        setEvents(prev => [...prev, duplicatedEvent]);
        setShowEditModal(false);
        setSelectedEvent(null);
        setHasChanges(true);
    };

    // Duplicate to all days of the week
    const duplicateToAllDays = () => {
        if (!selectedEvent) return;

        const startDate = new Date(selectedEvent.start);
        const endDate = new Date(selectedEvent.end);
        const originalDay = startDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

        // Parse weekStartDate with time to avoid UTC interpretation
        const monday = new Date(weekStartDate + 'T12:00:00');
        const newEvents: TimetableEvent[] = [];

        for (let i = 0; i < 5; i++) { // Mon to Fri
            const dayOffset = i; // Monday = 0
            if (dayOffset + 1 === originalDay) continue; // Skip original day (weekday: 1=Mon, 2=Tue...)

            const targetDate = new Date(monday);
            targetDate.setDate(monday.getDate() + i);

            const newStart = new Date(targetDate);
            newStart.setHours(startDate.getHours(), startDate.getMinutes(), 0, 0);

            const newEnd = new Date(targetDate);
            newEnd.setHours(endDate.getHours(), endDate.getMinutes(), 0, 0);

            newEvents.push({
                ...selectedEvent,
                id: `evt_${Date.now()}_${i}`,
                start: toLocalISOString(newStart),
                end: toLocalISOString(newEnd),
            });
        }

        setEvents(prev => [...prev, ...newEvents]);
        setShowEditModal(false);
        setSelectedEvent(null);
        setHasChanges(true);
    };

    const saveTimetable = async () => {
        setIsSaving(true);
        try {
            const eventsData = events.map(evt => ({
                id: evt.id || `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                title: evt.title || '',
                start: evt.start,
                end: evt.end,
                teacher: evt.extendedProps?.teacher || '',
                room: evt.extendedProps?.room || '',
                type: evt.extendedProps?.type || 'lesson'
            }));

            console.log('[TimetableEditor] Saving timetable:', {
                eventCount: eventsData.length,
                weekNumber,
                targetId
            });

            if (onSave) {
                // Use external save handler (from management screen)
                await onSave(eventsData);
                setHasChanges(false); // Reset after successful save
            } else {
                // Standalone save to Firestore
                const timetableId = `${targetType}_${targetId}_${term.replace(/\s+/g, '_')}_week${weekNumber}`;
                const timetableData = {
                    type: targetType,
                    targetId: targetId,
                    targetName: targetName,
                    term: term,
                    weekNumber: weekNumber,
                    weekStartDate: weekStartDate,
                    events: eventsData,
                    uploadedAt: Timestamp.now(),
                    lastModified: Timestamp.now()
                };
                await setDoc(doc(db, 'timetables', timetableId), timetableData);
                setHasChanges(false);
            }
        } catch (error) {
            console.error('Error saving timetable:', error);
            alert('Failed to save timetable. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const addQuickBreak = (type: 'break' | 'lunch') => {
        // Add a break/lunch for all 5 days at a common time
        const times = type === 'break'
            ? { start: '09:30', end: '10:00' }
            : { start: '12:00', end: '12:45' };

        // Parse weekStartDate with time to avoid UTC interpretation
        const monday = new Date(weekStartDate + 'T12:00:00');
        const newEvents: TimetableEvent[] = [];

        for (let i = 0; i < 5; i++) {
            const day = new Date(monday);
            day.setDate(monday.getDate() + i);
            // Use local date formatting instead of toISOString()
            const year = day.getFullYear();
            const month = String(day.getMonth() + 1).padStart(2, '0');
            const dayNum = String(day.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${dayNum}`;

            newEvents.push({
                id: `evt_${Date.now()}_${i}`,
                title: type === 'break' ? 'Break' : 'Lunch',
                start: `${dateStr}T${times.start}:00`,
                end: `${dateStr}T${times.end}:00`,
                backgroundColor: typeColors[type].bg,
                borderColor: typeColors[type].border,
                extendedProps: {
                    teacher: '',
                    room: '',
                    type: type
                }
            });
        }

        setEvents(prev => [...prev, ...newEvents]);
        setHasChanges(true);
    };

    return (
        <div className="timetable-editor">
            <div className="timetable-editor-header">
                <div className="header-left">
                    <h2>📅 {targetName} - {term} Week {weekNumber}</h2>
                    <p>Click on a time slot to add a lesson. Drag events to reschedule.</p>
                </div>
                <div className="header-right">
                    <button
                        className="quick-btn break"
                        onClick={() => addQuickBreak('break')}
                        title="Add break to all days"
                    >
                        ☕ Add Breaks
                    </button>
                    <button
                        className="quick-btn lunch"
                        onClick={() => addQuickBreak('lunch')}
                        title="Add lunch to all days"
                    >
                        🍽️ Add Lunches
                    </button>
                    <button
                        className="save-btn"
                        onClick={saveTimetable}
                        disabled={isSaving || !hasChanges}
                    >
                        {isSaving ? 'Saving...' : hasChanges ? '💾 Save Timetable' : '✓ Saved'}
                    </button>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>
            </div>

            <div className="timetable-editor-body">
                {isLoading ? (
                    <div className="loading-state">Loading timetable...</div>
                ) : (
                    <FullCalendar
                        ref={calendarRef}
                        plugins={[timeGridPlugin, interactionPlugin]}
                        initialView="timeGridWeek"
                        initialDate={weekStartDate}
                        headerToolbar={false}
                        allDaySlot={false}
                        weekends={false}
                        slotMinTime="07:30:00"
                        slotMaxTime="16:30:00"
                        slotDuration="00:15:00"
                        slotLabelInterval="00:30:00"
                        slotLabelFormat={{
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        }}
                        dayHeaderFormat={{ weekday: 'long' }}
                        height="calc(100vh - 200px)"
                        events={events}
                        editable={true}
                        selectable={true}
                        selectMirror={true}
                        select={handleDateSelect}
                        eventClick={handleEventClick}
                        eventDrop={handleEventDrop}
                        eventResize={handleEventResize}
                        eventContent={(eventInfo) => (
                            <div className="fc-event-content-custom">
                                <div className="event-title">{eventInfo.event.title}</div>
                                {eventInfo.event.extendedProps.teacher && (
                                    <div className="event-teacher">{eventInfo.event.extendedProps.teacher}</div>
                                )}
                                {eventInfo.event.extendedProps.room && (
                                    <div className="event-room">{eventInfo.event.extendedProps.room}</div>
                                )}
                            </div>
                        )}
                    />
                )}
            </div>

            {/* Add Lesson Modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="lesson-modal" onClick={e => e.stopPropagation()}>
                        <h3>➕ Add Lesson</h3>

                        <div className="form-group">
                            <label>Type</label>
                            <div className="type-buttons">
                                {(['lesson', 'break', 'lunch', 'assembly'] as const).map(type => (
                                    <button
                                        key={type}
                                        className={`type-btn ${lessonForm.type === type ? 'active' : ''}`}
                                        style={{
                                            backgroundColor: lessonForm.type === type ? typeColors[type].bg : 'transparent',
                                            borderColor: typeColors[type].bg
                                        }}
                                        onClick={() => setLessonForm(prev => ({ ...prev, type }))}
                                    >
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Subject / Title *</label>
                            <input
                                type="text"
                                value={lessonForm.title}
                                onChange={e => setLessonForm(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="e.g., Mathematics"
                                autoFocus
                            />
                        </div>

                        {lessonForm.type === 'lesson' && (
                            <>
                                <div className="form-group">
                                    <label>Teacher</label>
                                    <input
                                        type="text"
                                        value={lessonForm.teacher}
                                        onChange={e => setLessonForm(prev => ({ ...prev, teacher: e.target.value }))}
                                        placeholder="e.g., Ms. Jane"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Room</label>
                                    <input
                                        type="text"
                                        value={lessonForm.room}
                                        onChange={e => setLessonForm(prev => ({ ...prev, room: e.target.value }))}
                                        placeholder="e.g., Room 101"
                                    />
                                </div>
                            </>
                        )}

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => setShowAddModal(false)}>Cancel</button>
                            <button
                                className="add-btn"
                                onClick={addLesson}
                                disabled={!lessonForm.title.trim()}
                            >
                                Add Lesson
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Lesson Modal */}
            {showEditModal && selectedEvent && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="lesson-modal" onClick={e => e.stopPropagation()}>
                        <h3>✏️ Edit Lesson</h3>

                        <div className="form-group">
                            <label>Type</label>
                            <div className="type-buttons">
                                {(['lesson', 'break', 'lunch', 'assembly'] as const).map(type => (
                                    <button
                                        key={type}
                                        className={`type-btn ${lessonForm.type === type ? 'active' : ''}`}
                                        style={{
                                            backgroundColor: lessonForm.type === type ? typeColors[type].bg : 'transparent',
                                            borderColor: typeColors[type].bg
                                        }}
                                        onClick={() => setLessonForm(prev => ({ ...prev, type }))}
                                    >
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Subject / Title *</label>
                            <input
                                type="text"
                                value={lessonForm.title}
                                onChange={e => setLessonForm(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="e.g., Mathematics"
                            />
                        </div>

                        {lessonForm.type === 'lesson' && (
                            <>
                                <div className="form-group">
                                    <label>Teacher</label>
                                    <input
                                        type="text"
                                        value={lessonForm.teacher}
                                        onChange={e => setLessonForm(prev => ({ ...prev, teacher: e.target.value }))}
                                        placeholder="e.g., Ms. Jane"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Room</label>
                                    <input
                                        type="text"
                                        value={lessonForm.room}
                                        onChange={e => setLessonForm(prev => ({ ...prev, room: e.target.value }))}
                                        placeholder="e.g., Room 101"
                                    />
                                </div>
                            </>
                        )}

                        <div className="modal-actions">
                            <button className="delete-btn" onClick={deleteLesson}>🗑️ Delete</button>
                            <div className="duplicate-actions">
                                <button className="duplicate-btn" onClick={duplicateLesson} title="Create a copy of this event">
                                    📋 Duplicate
                                </button>
                                <button className="duplicate-btn secondary" onClick={duplicateToNextDay} title="Copy to next day">
                                    ➡️ Next Day
                                </button>
                                <button className="duplicate-btn secondary" onClick={duplicateToAllDays} title="Copy to all weekdays">
                                    📅 All Days
                                </button>
                            </div>
                            <button className="cancel-btn" onClick={() => setShowEditModal(false)}>Cancel</button>
                            <button
                                className="add-btn"
                                onClick={updateLesson}
                                disabled={!lessonForm.title.trim()}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimetableEditor;
